/**
 * navItems.js — Central catalog of sidebar entries.
 *
 * Extracted from `components/layout/Sidebar.jsx` (CEO 2026-09-30) so the
 * admin user editor can enumerate every tab and let the admin explicitly
 * check/uncheck what a given user should see.
 *
 * Rules applied at render time (Sidebar):
 *   • item shown IF (any of its `roles` is in user.effectiveRoles OR
 *     extraAccess(user) returns true) AND item.to NOT in user.hidden_nav
 *
 * This keeps the historical role-based defaults but lets an admin
 * override per-user without changing the base role.
 */
import {
    AlertTriangle,
    Archive,
    BarChart3,
    Bell,
    BookmarkPlus,
    BookOpen,
    Briefcase,
    Building2,
    CalendarDays,
    CheckCircle2,
    ClipboardCheck,
    ClipboardEdit,
    Cloud,
    FileText,
    Fingerprint,
    FlaskConical,
    Flame,
    Globe,
    Headphones,
    LayoutDashboard,
    LineChart,
    Mail,
    Megaphone,
    MessageSquare,
    Package,
    Settings,
    ShieldCheck,
    SlidersHorizontal,
    Sparkles,
    Trash2,
    TrendingUp,
    Trophy,
    UserCircle2,
    Users,
    UserX,
} from "lucide-react";


export const NAV_ITEMS = [
    { to: "/dashboard", label: "Cockpit", icon: LayoutDashboard, roles: ["pre_recruteur", "referent", "admin"] },
    {
        to: "/candidats",
        label_by_role: { pre_recruteur: "Mes appels", referent: "Mes immersions", admin: "Candidats", coordinateur: "Candidats" },
        label: "Candidats",
        icon: Users,
        roles: ["pre_recruteur", "referent", "admin"],
        // iter110m-hotfix5 · Coordinateur RH voit "Candidats" (scopé par
        // ses régions via role_scope_query). Permet l'onboarding express
        // directement depuis la fiche candidat au lieu de passer
        // uniquement par /embauche.
        extraAccess: (u) => u?.role === "coordinateur",
    },
    { to: "/offres", label: "Offres", icon: Megaphone, roles: ["pre_recruteur", "admin"] },
    // iter100 · Nouveau parcours Growth (isolé du parcours RD existant)
    { to: "/growth", label: "Growth · BD & Alt.", icon: Sparkles, roles: ["pre_recruteur", "admin"] },
    { to: "/growth/shortlist", label: "Shortlist Direction", icon: TrendingUp, roles: ["admin"] },
    { to: "/calendrier-immersions", label: "Calendrier", icon: CalendarDays, roles: ["pre_recruteur", "referent", "admin"] },
    { to: "/mes-immersions", label: "Mes immersions", icon: Trophy, roles: ["pre_recruteur", "admin"] },
    { to: "/vivier", label: "Vivier", icon: BookmarkPlus, roles: ["pre_recruteur", "admin"] },
    { to: "/ecartes", label: "Écartés", icon: Archive, roles: ["pre_recruteur", "referent", "admin"], extraAccess: (u) => u?.role === "coordinateur" && !!u?.can_remove_from_pipeline },
    { to: "/refuses", label: "Refusés", icon: UserX, roles: ["pre_recruteur", "referent", "admin"] },
    { to: "/pepites", label: "Pépites", icon: Flame, roles: ["pre_recruteur", "admin"] },
    { to: "/alertes", label: "Alertes", icon: AlertTriangle, roles: ["pre_recruteur", "referent", "admin"] },
    { to: "/embauche", label: "Embauche & Contrats", icon: ClipboardCheck, roles: ["admin"], extraAccess: (u) => !!u?.can_access_embauche || !!u?.can_send_onboarding || !!u?.can_review_onboarding || !!u?.can_view_contracts || !!u?.can_download_badges || !!u?.can_manage_contracts },
    { to: "/permanents", label: "Permanents indépendants", icon: UserCircle2, roles: ["admin"] },
    { to: "/materiel", label: "Matériel · bons de remise", icon: Package, roles: ["admin", "coordinateur", "referent"] },
    { to: "/mes-bons", label: "Mes bons de matériel", icon: ClipboardCheck, roles: ["pre_recruteur", "referent", "coordinateur"] },
    { to: "/procedures", label: "Procédures internes", icon: BookOpen, roles: ["admin", "referent"] },
    { to: "/admin/codes-rd", label: "Codes RD", icon: Fingerprint, roles: ["admin"] },
    { to: "/appels", label: "Centre d'appels", icon: Headphones, roles: ["pre_recruteur", "admin"] },
    { to: "/analytics-appels", label: "Analytics Appels", icon: LineChart, roles: ["pre_recruteur", "admin"] },
    { to: "/notifications", label: "Notifications", icon: Bell, roles: ["pre_recruteur", "referent", "admin"] },
    { to: "/utilisateurs", label: "Équipe", icon: Briefcase, roles: ["admin"] },
    { to: "/admin/analyses", label: "Analyses équipe", icon: BarChart3, roles: ["admin"] },
    { to: "/admin/connexions", label: "Historique connexions", icon: ShieldCheck, roles: ["admin"] },
    { to: "/admin/trame", label: "Trame entretien", icon: ClipboardEdit, roles: ["admin"] },
    { to: "/admin/modeles-messages", label: "Modèles SMS", icon: MessageSquare, roles: ["admin"] },
    { to: "/admin/parametres-entreprise", label: "Paramètres entreprise", icon: Settings, roles: ["admin"] },
    { to: "/admin/roles-regions", label: "Rôles & régions", icon: SlidersHorizontal, roles: ["admin"] },
    { to: "/admin/modele-contrat", label: "Modèle contrat", icon: FileText, roles: ["admin"] },
    { to: "/admin/associations", label: "Associations partenaires", icon: Building2, roles: ["admin"] },
    { to: "/admin/emails", label: "Historique emails", icon: Mail, roles: ["admin"] },
    { to: "/admin/corbeille", label: "Corbeille", icon: Trash2, roles: ["admin"] },
    { to: "/admin/candidat-test", label: "Candidat test", icon: FlaskConical, roles: ["admin"] },
    { to: "/integrations/layan", label: "Layan", icon: Cloud, roles: ["admin"] },
    { to: "/integrations/site", label: "Site Shariti", icon: Globe, roles: ["admin"] },
];


/** Retourne le sous-ensemble de NAV_ITEMS visible pour un `user` donné,
 *  en tenant compte de son rôle, de ses `extraAccess`, ET de son
 *  `hidden_nav` (l'admin coche/décoche onglet par onglet). */
export function eligibleNavItems(user, userRoles) {
    if (!user) return [];
    const hidden = new Set(user.hidden_nav || []);
    return NAV_ITEMS.filter((i) => {
        const roleOk = i.roles.some((r) => userRoles.has(r))
            || (typeof i.extraAccess === "function" && i.extraAccess(user));
        return roleOk && !hidden.has(i.to);
    });
}
